import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AuthLayout from "@/components/auth/AuthLayout";
import LoginForm from "@/components/auth/LoginForm";
import ResetPasswordForm from "@/components/auth/ResetPasswordForm";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/ui/use-toast";

const SignIn = () => {
  const [isResettingPassword, setIsResettingPassword] = React.useState(false);
  const navigate = useNavigate();
  const { user, isEmployee, loading } = useAuth();

  // Handle URL hash errors and recovery type on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const isRecoveryType = params.get('type') === 'recovery';

    // Handle auth errors from URL hash (e.g., expired links)
    const error = params.get('error');
    const errorCode = params.get('error_code');
    const errorDescription = params.get('error_description');

    if (error) {
      const message = errorDescription
        ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
        : 'An authentication error occurred';

      toast.error("Authentication Error", {
        description: errorCode === 'otp_expired'
          ? "The email link has expired. Please request a new one."
          : message,
      });

      // Clean up the URL hash
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    if (isRecoveryType) {
      setIsResettingPassword(true);
      return;
    }

    // Listen for PASSWORD_RECOVERY and SIGNED_OUT events only
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === 'PASSWORD_RECOVERY') {
          setIsResettingPassword(true);
        } else if (event === 'SIGNED_OUT') {
          setIsResettingPassword(false);
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Handle redirect when user state updates from AuthContext
  // This ensures navigation happens AFTER auth state has propagated to all components
  useEffect(() => {
    // Skip if loading or in password reset flow
    if (loading || isResettingPassword) return;

    // Skip if no user (not logged in)
    if (!user) return;

    // Double-check URL hash for recovery type or errors
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    if (hashParams.get('type') === 'recovery') return;
    if (hashParams.get('error')) return;

    const handleAuthRedirect = async () => {
      if (isEmployee) {
        navigate("/employee-portal", { replace: true });
        return;
      }

      // Check if user has an active subscription first
      const { data: subscription } = await supabase
        .from('user_subscriptions')
        .select('status')
        .eq('user_id', user.id)
        .in('status', ['active', 'trialing'])
        .maybeSingle();

      // No subscription - redirect to plan selection
      // Preserve any plan query param from pricing page
      if (!subscription) {
        const urlParams = new URLSearchParams(window.location.search);
        const planParam = urlParams.get('plan');
        const selectPlanUrl = planParam ? `/select-plan?plan=${planParam}` : '/select-plan';
        navigate(selectPlanUrl, { replace: true });
        return;
      }

      // Check if user has any businesses
      const { data: businesses } = await supabase
        .from('business_users')
        .select('business_id')
        .eq('user_id', user.id)
        .limit(1);

      // Redirect to create-business if no businesses, otherwise to dashboard
      if (businesses && businesses.length === 0) {
        navigate("/create-business", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    };

    handleAuthRedirect();
  }, [user, loading, isEmployee, isResettingPassword, navigate]);

  const handleSwitchToSignUp = () => {
    navigate("/sign-up");
  };

  return (
    <AuthLayout title={isResettingPassword ? "Set New Password" : "Sign in to your account"} hideGoogleAuth={isResettingPassword}>
      {isResettingPassword ? (
        <ResetPasswordForm onPasswordSet={() => {
          setIsResettingPassword(false);
          navigate('/sign-in', { replace: true });
        }} />
      ) : (
        <LoginForm onSwitchForm={handleSwitchToSignUp} />
      )}
    </AuthLayout>
  );
};

export default SignIn;